import { memo } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import {
  FaInfoCircle,
  FaChartLine,
  FaSolarPanel,
  FaTemperatureHigh,
  FaWater,
} from "react-icons/fa";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.8 } },
};

const stepVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (custom) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.1 * custom, duration: 0.5 },
  }),
};

const calloutVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { delay: 0.2, duration: 0.5 } },
};

const TechStep = memo(
  ({
    index,
    color,
    icon: Icon,
    title,
    description,
    stat,
    statIcon: StatIcon,
  }) => (
    <motion.div
      className={`relative bg-gray-800/50 rounded-xl overflow-hidden border border-gray-700`}
      custom={index}
      variants={stepVariants}
      viewport={{ once: true }}
    >
      <div
        className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-${color}-500 to-${color}-400`}
      ></div>
      <div className="p-6">
        <div className="flex items-center mb-4">
          <div
            className={`w-12 h-12 flex items-center justify-center rounded-full bg-${color}-500/20 text-${color}-400 mr-4`}
          >
            <Icon size={24} />
          </div>
          <h3 className="text-xl font-semibold text-white">{title}</h3>
        </div>
        <div className="space-y-4">
          <p className="text-gray-300 leading-relaxed">{description}</p>
          <div
            className={`flex items-center text-${color}-400 text-sm font-semibold`}
          >
            <StatIcon className="mr-1.5" />
            <span>{stat}</span>
          </div>
        </div>
      </div>
    </motion.div>
  )
);

TechStep.displayName = "TechStep";

TechStep.propTypes = {
  index: PropTypes.number.isRequired,
  color: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  stat: PropTypes.string.isRequired,
  statIcon: PropTypes.elementType.isRequired,
};

const PVTTechnology = ({ detailData, _pvtImage1, _pvtImage }) => {
  const { t } = useTranslation();
  const pvtTechData = t("components.pvt.pvtTechnology", {
    returnObjects: true,
  });

  const panelData = detailData
    ? {
        introText: detailData.panelData.introText,
        additionalInfo: detailData.panelData.additionalInfo,
        classification: detailData.panelData.classification,
      }
    : null;

  if (!panelData) return null;

  const steps = [
    {
      color: "yellow",
      icon: FaSolarPanel,
      title: pvtTechData.steps.step1.title,
      description: panelData.introText,
      stat: pvtTechData.steps.step1.stat,
      statIcon: FaTemperatureHigh,
    },
    {
      color: "red",
      icon: FaTemperatureHigh,
      title: pvtTechData.steps.step2.title,
      description: panelData.additionalInfo,
      stat: pvtTechData.steps.step2.stat,
      statIcon: FaChartLine,
    },
    {
      color: "blue",
      icon: FaWater,
      title: pvtTechData.steps.step3.title,
      description: pvtTechData.steps.step3.description,
      stat: pvtTechData.steps.step3.stat,
      statIcon: FaChartLine,
    },
  ];

  return (
    <motion.div
      className="mb-16"
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "0px 0px -100px 0px" }}
      id="safePad"
    >
      <div className="bg-gradient-to-b from-gray-900 to-gray-800 rounded-xl overflow-hidden shadow-xl border border-gray-700">
        <div className="relative py-8 text-center border-b border-gray-700">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-2xl md:text-3xl font-bold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-gray-200 via-blue-100 to-gray-200">
              {pvtTechData.title}
            </h2>
            <p className="text-gray-400 max-w-3xl mx-auto">
              {pvtTechData.subtitle}
            </p>
            <div className="absolute left-0 right-0 bottom-0 h-px bg-gradient-to-r from-transparent via-blue-500 to-transparent"></div>
          </div>
        </div>

        <div className="p-6 md:p-8">
          <div className="max-w-3xl mx-auto mb-10 text-center">
            <p className="text-gray-300 leading-relaxed text-lg">
              {pvtTechData.introduction}
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 mb-12">
            {steps.map((step, index) => (
              <TechStep key={index} index={index + 1} {...step} />
            ))}
          </div>

          <motion.div
            className="relative max-w-4xl mx-auto bg-gray-800/30 rounded-xl border border-gray-700 p-6 md:p-8"
            variants={calloutVariants}
            viewport={{ once: true }}
          >
            <div className="flex items-start space-x-4">
              <FaInfoCircle className="text-blue-400 text-xl flex-shrink-0 mt-1" />
              <div>
                <h4 className="text-xl font-semibold text-white mb-2">
                  {pvtTechData.designSpec.title}
                </h4>
                <p className="text-gray-300 leading-relaxed">
                  <span className="text-blue-400 font-medium">
                    {pvtTechData.designSpec.unglazedLabel}
                  </span>{" "}
                  {panelData.classification}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

export default memo(PVTTechnology);

PVTTechnology.propTypes = {
  detailData: PropTypes.shape({
    panelData: PropTypes.shape({
      introText: PropTypes.string.isRequired,
      additionalInfo: PropTypes.string.isRequired,
      classification: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  _pvtImage1: PropTypes.any,
  _pvtImage: PropTypes.any,
};
